import { useEffect, useState } from "react";
import type { AgentDTO, SubjectDTO } from "../../shared/types";
import { get, post } from "../lib/api";
import { Link } from "../lib/router";
import { pluralWord } from "../../shared/scope-words";
import { scopePath, shortKey, typePath, useScopeTypes, type AgentScopeRow } from "../lib/scopes";
import { List, ListRow } from "./List";
import { ScopePicker } from "./ScopePicker";
import { TextLink } from "./TextLink";
import { Badge, Button, ErrorText, Panel, StatusBadge, timeAgo } from "./ui";

/**
 * The agent page's scopes tab, named after its type ("Accounts"): its type, where each scope it works under stands — the
 * last run's answer, its pending requests and next wake, as its agent level reads them with `pandas scope status` — and
 * putting it under another.
 */
export function AgentScopes({ agent }: { agent: AgentDTO }) {
  const { types } = useScopeTypes();
  const [rows, setRows] = useState<AgentScopeRow[] | null>(null);
  const [picked, setPicked] = useState<SubjectDTO | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const own = (types ?? []).filter((t) => agent.scopeTypes.includes(t.key));
  const typeName = (key: string) => types?.find((t) => t.key === key)?.name ?? key;
  const plural = own[0] ? pluralWord(own[0].name) : "Scopes";

  const load = () => get<AgentScopeRow[]>(`/api/agents/${agent.id}/scopes?detail=1`).then(setRows, (e) => setError(e.message));
  useEffect(() => {
    void load();
  }, [agent.id]);

  const putUnder = async () => {
    if (!picked) return;
    setBusy(true);
    setError(null);
    try {
      await post(`/api/agents/${agent.id}/scopes`, { subjectId: picked.id });
      setPicked(null);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
      <List
        title={rows ? `${plural} it works under · ${rows.length}` : `${plural} it works under`}
        hint="Where each stands: its last run, what waits for a person, and its next wake. Under each it has its own conversation and runs one at a time, reading the scope's memory, skills and files."
        empty={
          rows?.length === 0
            ? agent.scopeTypes.length
              ? `${agent.name} works under no scope yet. Put it under one, or it creates its own.`
              : `${agent.name} has access to no scope type, so it always works on its own.`
            : undefined
        }
        footer={error && <ErrorText error={error} />}
      >
        {rows?.map((s) => (
          <ListRow
            key={s.id}
            title={
              <>
                <TextLink quiet to={scopePath({ kind: s.kind, id: s.subjectId })} className="truncate text-sm font-medium">
                  {s.displayName || shortKey(s.key)}
                </TextLink>
                <Badge>{typeName(s.kind)}</Badge>
              </>
            }
            trailing={
              s.conversationId && (
                <TextLink to={`/admin/conversations/${s.conversationId}`}>
                  Conversation
                </TextLink>
              )
            }
          >
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted">
              <span className="font-mono" title={s.key}>{shortKey(s.key)}</span>
              <span>·</span>
              <span>{s.runCount} {s.runCount === 1 ? "run" : "runs"}</span>
              {s.lastRunStatus && (
                <>
                  <StatusBadge status={s.lastRunStatus} />
                  <span>{timeAgo(s.lastRunAt)}</span>
                </>
              )}
              {Object.entries(s.pending).map(([kind, n]) => (
                <Badge key={kind} tone="amber">{n} {kind} waiting</Badge>
              ))}
              {s.nextWake && <span title={s.nextWake.key ?? undefined}>· wakes {new Date(s.nextWake.at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}</span>}
            </div>
            {s.lastRunAnswer && <p className="mt-0.5 truncate text-xs text-faint" title={s.lastRunAnswer}>{s.lastRunAnswer}</p>}
          </ListRow>
        ))}
      </List>
      <div className="min-w-0 space-y-4">
        <Panel title="Scope type" hint="It works under, and may create, scopes of this type only.">
          <div className="flex flex-wrap gap-2">
            {own.map((t) => (
              <Link key={t.key} to={typePath(t.key)} className="rounded-full bg-fill px-3 py-1 text-sm font-medium transition hover:bg-fill-strong">
                {t.name}
              </Link>
            ))}
          </div>
          {types && own.length === 0 && <p className="text-sm text-muted">None yet. Give it one in Settings → Scope type, or on a type's page.</p>}
        </Panel>
        {own.length > 0 && (
          <Panel title="Add scope" hint={`Put ${agent.name} under a scope of its type.`}>
            <div className="space-y-3">
              <ScopePicker types={own} value={picked} onChange={setPicked} />
              <Button variant="primary" disabled={busy || !picked} onClick={putUnder}>
                {busy ? "Adding…" : "Put it under this scope"}
              </Button>
            </div>
          </Panel>
        )}
      </div>
    </div>
  );
}
